import type { MutableRefObject, ReactNode } from "react";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import PropTypes from "prop-types";

import type { OdigeoRouterNavigate } from "./router/router";
import { OdigeoRouterNavigateProvider } from "./router/RouterProvider";
import type { PageHandlerType } from "./types";

export type PageState = Record<
  string,
  {
    state?: string;
    delay?: number;
    params?: Record<string, string>;
  }
>;

interface NavigateOptions {
  push?: unknown;
  state?: unknown;
}

interface HistoryState {
  navigate: (page: string, options: NavigateOptions) => void;
}

interface CallbacksRecord {
  forward: Array<() => void>;
  back: Array<() => void>;
  none: Array<never>;
}

type PopstateType = "back" | "forward" | "none";

export type RegisterPageCallback = (options: {
  page: string;
  delay: number;
}) => void;

export type DispatchActionFn = (
  action: Omit<Action, "registeredPages">,
) => void;

const PageContext = createContext<PageState | undefined>(undefined);
const NavigationContext = createContext<HistoryState | undefined>(undefined);
const CallbacksContext = createContext<
  MutableRefObject<CallbacksRecord> | undefined
>(undefined);
export const RegisterPageContext = createContext<
  RegisterPageCallback | undefined
>(undefined);
export const ActionsContext = createContext<DispatchActionFn | undefined>(
  undefined,
);

const createClosingPages = (
  pageState: PageState,
  {
    registeredPages,
    delay,
  }: { registeredPages: PageState; delay: number | undefined },
) => {
  return Object.keys(pageState).reduce((result, page) => {
    const computedDelay = delay || registeredPages[page]?.delay;
    if (computedDelay) {
      result[page] = {
        state: "closing",
        delay: computedDelay,
      };
    }
    return result;
  }, {} as PageState);
};

export interface Action {
  type: string;
  registeredPages: PageState;
  targetPage: string;
  targetPageParams?: Record<string, string>;
}

const openingReducer = (
  pageState: PageState,
  { registeredPages, targetPage, targetPageParams }: Action,
) => {
  const delay = registeredPages[targetPage]?.delay;
  const nextPageState = createClosingPages(pageState, {
    registeredPages,
    delay,
  });
  nextPageState[targetPage] = {
    state: "open",
    params: targetPageParams,
  };
  return nextPageState;
};

const closeReducer = (
  pageState: PageState,
  { targetPage }: { targetPage: string },
) => {
  return Object.keys(pageState).reduce((result, page: string) => {
    if (targetPage !== page) {
      result[page] = pageState[page];
    }
    return result;
  }, {} as PageState);
};

const reducer = (pages: PageState, action: Action) => {
  switch (action.type) {
    case "opening":
      return openingReducer(pages, action);
    case "close":
      return closeReducer(pages, action);
    default:
      return pages;
  }
};

const calculateInitialPageState = (initialPage: string | undefined) => {
  return initialPage
    ? {
        [initialPage]: {
          state: "open",
        },
      }
    : {};
};

interface UseInternalHistoryOptions {
  navigate: PageHandlerType["navigate"];
  callbacksRef: MutableRefObject<CallbacksRecord>;
}

const useInternalHistory = ({
  navigate,
  callbacksRef,
}: UseInternalHistoryOptions) => {
  const positionRef = useRef(0);
  useEffect(() => {
    const getPopstateType = (currentPosition: number): PopstateType => {
      if (positionRef.current > currentPosition) {
        return "back";
      }
      if (positionRef.current < currentPosition) {
        return "forward";
      }
      return "none";
    };
    const onPopstate = () => {
      const currentPosition: number = window.history.state?.position || 0;
      const popStateType = getPopstateType(currentPosition);
      positionRef.current = currentPosition;
      const callbacks = callbacksRef.current[popStateType] || [];
      callbacks.forEach((callback) => callback());
    };
    window.addEventListener("popstate", onPopstate);
    return () => {
      window.removeEventListener("popstate", onPopstate);
    };
  }, [callbacksRef, positionRef]);

  return useMemo(() => {
    return {
      navigate(page: string, { push, state }: NavigateOptions = {}) {
        navigate(page);
        if (push) {
          window.history.pushState(
            {
              state,
              position: ++positionRef.current,
            },
            "",
            undefined,
          );
        } else {
          window.history.replaceState(
            {
              state,
              position: positionRef.current,
            },
            "",
            undefined,
          );
        }
      },
    };
  }, [navigate, positionRef]);
};

interface PageStateProviderProps {
  odigeoNavigate: OdigeoRouterNavigate;
  children: ReactNode;
  navigate: PageHandlerType["navigate"];
  onChange: PageHandlerType["onChange"];
  stopOnChange: PageHandlerType["stopOnChange"];
  initialPage?: PageHandlerType["initialPage"];
}

const PageStateProvider = ({
  navigate,
  onChange,
  stopOnChange,
  initialPage,
  children,
  odigeoNavigate,
}: PageStateProviderProps) => {
  const [pageState, dispatch] = useReducer(
    reducer,
    calculateInitialPageState(initialPage),
  );
  const callbacksRef = useRef<CallbacksRecord>({
    back: [],
    forward: [],
    none: [],
  });
  const registeredPagesRef = useRef<PageState>({});
  const registerPage = useCallback(
    ({ page, delay }: { page: string; delay: number }) => {
      registeredPagesRef.current[page] = { delay };
    },
    [registeredPagesRef],
  );
  const dispatchAction = useCallback(
    (props: Omit<Action, "registeredPages">) =>
      dispatch({
        ...props,
        registeredPages: registeredPagesRef.current,
      }),
    [dispatch, registeredPagesRef],
  );

  useEffect(() => {
    onChange((page, params) => {
      dispatchAction({
        type: "opening",
        targetPage: page,
        targetPageParams: params,
      });
    });
    return stopOnChange;
  }, [onChange, stopOnChange, dispatchAction]);

  const history = useInternalHistory({ navigate, callbacksRef });
  return (
    <PageContext.Provider value={pageState}>
      <CallbacksContext.Provider value={callbacksRef}>
        <OdigeoRouterNavigateProvider odigeoNavigate={odigeoNavigate}>
          <NavigationContext.Provider value={history}>
            <ActionsContext.Provider value={dispatchAction}>
              <RegisterPageContext.Provider value={registerPage}>
                {children}
              </RegisterPageContext.Provider>
            </ActionsContext.Provider>
          </NavigationContext.Provider>
        </OdigeoRouterNavigateProvider>
      </CallbacksContext.Provider>
    </PageContext.Provider>
  );
};

const createUseHistoryMovementEffect = (type: keyof CallbacksRecord) => {
  return (callback: () => void) => {
    const callbacksRef = useContext(CallbacksContext);
    const callbacks = callbacksRef?.current;
    useEffect(() => {
      if (!callbacks || type === "none") {
        return;
      }
      callbacks[type].push(callback);
      return () => {
        callbacks[type] = callbacks[type].filter((cb) => cb !== callback);
      };
    }, [callback, callbacks]);
  };
};

export const useHistoryBackEffect = createUseHistoryMovementEffect("back");
export const useHistoryForwardEffect =
  createUseHistoryMovementEffect("forward");

export const useHistory = () => useContext(NavigationContext);
export const useNavigation = () => useHistory()?.navigate;
export const useInternalPageState = () => useContext(PageContext);

export default PageStateProvider;

PageStateProvider.propTypes = {
  navigate: PropTypes.func,
  odigeoNavigate: PropTypes.func,
  initialPage: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  stopOnChange: PropTypes.func.isRequired,
  children: PropTypes.node,
};
